import { useEffect, useMemo, useRef, useState } from "react";
import type { AppSettings } from "@/lib/queries";
import { useSlotConfigs, useSlotConfigPreview, useSlotConfigRevertPreview, type SlotConfigVersion, type SlotConfigPreviewRow, type SlotConfigPreview, type SlotConfigRevertPreviewRow, type SlotConfigBackupStatus } from "@/lib/queries";
import { apiFetch } from "@/lib/api";
import {
  useCreateSlotConfig,
  useUpdateSlotConfig,
  useDeleteSlotConfig,
  useUpdateSlotConfigOperational,
  useTriggerSlotConfigBackup,
  useApplySlotConfig,
  useSuggestAssignments,
  useAutoFillAssignments,
  type SlotConfigPayload,
  type SlotConfigRemapReport,
} from "@/lib/mutations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, Plus, Trash2, Pencil, Play, DatabaseBackup, RefreshCw, ArrowLeft, ArrowUp, ArrowDown, Loader2, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";
import { todayIST } from "@emr/shared/india";
import { cn } from "@/lib/utils";

/**
 * Phase 4 (2026-10-18) — the config versions manager. Replaces the legacy
 * Morning/Evening globals editor (those fields are now only the legacy
 * fallback for pre-config DBs).
 *
 * Sections:
 *  1. Versions list — every config (applied + drafts) as cards; the ACTIVE
 *     config (resolves for today) is highlighted.
 *  2. Draft editor — create/edit future versions with dynamic slot rows.
 *  3. Operational editor (admin-only) — latePolicy + check-in windows on the
 *     ACTIVE config (PATCH /operational; session times/max are version-locked).
 *  4. The apply wizard — preview → backup (no skip; the API 422s without a
 *     fresh backup) → typed-confirmation apply → result report.
 */

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const LATE_POLICIES: { value: SlotConfigPayload["latePolicy"]; label: string; hint: string }[] = [
  { value: "first", label: "First", hint: "late patient goes to the front of the new slot's queue" },
  { value: "in_order", label: "In order", hint: "late patient keeps natural arrival order" },
  { value: "last", label: "Last", hint: "late patient goes to the end of the queue" },
];

function hhmmToMin(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

/** Tomorrow IST as YYYY-MM-DD — the min effectiveFrom (API 422s past dates). */
function tomorrowIST(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(Date.now() + 24 * 60 * 60 * 1000));
}

interface SlotRow {
  key: string;
  label: string;
  startTime: string;
  endTime: string;
  checkinStart: string;
  checkinEnd: string;
  maxAppts: string; // string in the form, parsed on save
  sortOrder: string; // string in the form
}

interface DraftForm {
  effectiveFrom: string;
  latePolicy: SlotConfigPayload["latePolicy"];
  slots: SlotRow[];
}

function emptySlotRow(order: number): SlotRow {
  return { key: "", label: "", startTime: "", endTime: "", checkinStart: "", checkinEnd: "", maxAppts: "30", sortOrder: String(order) };
}

/** Client-side validation mirroring the API's validateSlots rules. */
function validateDraft(form: DraftForm): string[] {
  const errors: string[] = [];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.effectiveFrom)) errors.push("Effective from date is required.");
  if (form.slots.length === 0) return ["At least one slot is required."];
  const keys = new Set<string>();
  const labels = new Set<string>();
  const orders = new Set<number>();
  for (const s of form.slots) {
    if (!s.key.trim()) errors.push("Slot key is required.");
    else if (keys.has(s.key)) errors.push(`Duplicate slot key: ${s.key}.`);
    else keys.add(s.key);
    if (!s.label.trim()) errors.push(`Label is required for slot ${s.key || "(unnamed)"}.`);
    else if (labels.has(s.label)) errors.push(`Duplicate slot label: ${s.label}.`);
    else labels.add(s.label);
    for (const [field, value] of [["startTime", s.startTime], ["endTime", s.endTime], ["checkinStart", s.checkinStart], ["checkinEnd", s.checkinEnd]] as const) {
      if (!HHMM_RE.test(value)) errors.push(`Slot ${s.key || "(unnamed)"}: ${field} must be HH:mm (24h).`);
    }
    if (HHMM_RE.test(s.startTime) && HHMM_RE.test(s.endTime) && hhmmToMin(s.startTime) > hhmmToMin(s.endTime)) {
      errors.push(`Slot ${s.key}: startTime must be <= endTime.`);
    }
    if (HHMM_RE.test(s.checkinStart) && HHMM_RE.test(s.checkinEnd) && hhmmToMin(s.checkinStart) > hhmmToMin(s.checkinEnd)) {
      errors.push(`Slot ${s.key}: checkinStart must be <= checkinEnd.`);
    }
    const max = parseInt(s.maxAppts, 10);
    if (Number.isNaN(max) || max < 0) errors.push(`Slot ${s.key}: maxAppts must be >= 0.`);
    const order = parseInt(s.sortOrder, 10);
    if (Number.isNaN(order)) errors.push(`Slot ${s.key}: sortOrder must be a number.`);
    else if (orders.has(order)) errors.push(`Duplicate sortOrder: ${order}.`);
    else orders.add(order);
  }
  // Non-overlapping session windows in sortOrder order.
  const timeValid = form.slots
    .filter((s) => HHMM_RE.test(s.startTime) && HHMM_RE.test(s.endTime) && !Number.isNaN(parseInt(s.sortOrder, 10)))
    .sort((a, b) => parseInt(a.sortOrder, 10) - parseInt(b.sortOrder, 10));
  for (let i = 1; i < timeValid.length; i++) {
    const prev = timeValid[i - 1];
    const cur = timeValid[i];
    if (hhmmToMin(cur.startTime) < hhmmToMin(prev.endTime)) {
      errors.push(`Session windows overlap: ${prev.key} [${prev.startTime}-${prev.endTime}) and ${cur.key} [${cur.startTime}-${cur.endTime}).`);
    }
  }
  return errors;
}

function draftFromVersion(v: SlotConfigVersion): DraftForm {
  return {
    effectiveFrom: v.effectiveFrom,
    latePolicy: v.latePolicy,
    slots: [...v.slots]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((s) => ({
        key: s.key,
        label: s.label,
        startTime: s.startTime,
        endTime: s.endTime,
        checkinStart: s.checkinStart,
        checkinEnd: s.checkinEnd,
        maxAppts: String(s.maxAppts),
        sortOrder: String(s.sortOrder),
      })),
  };
}

function draftToPayload(form: DraftForm): SlotConfigPayload {
  return {
    effectiveFrom: form.effectiveFrom,
    latePolicy: form.latePolicy,
    slots: form.slots.map((s) => ({
      key: s.key.trim(),
      label: s.label.trim(),
      startTime: s.startTime,
      endTime: s.endTime,
      checkinStart: s.checkinStart,
      checkinEnd: s.checkinEnd,
      maxAppts: parseInt(s.maxAppts, 10),
      sortOrder: parseInt(s.sortOrder, 10),
    })),
  };
}

// ---------------------------------------------------------------------------
// AssignmentPreviewTable (2026-10-18) — the apply wizard's Step-1 preview,
// FACTORED so the pending-plan Edit wizard and Delete dialog render the
// SAME format: rows table, needsAssignment dropdowns (slot options from the
// relevant config), Auto-fill + Refine with AI buttons (admin-only AI),
// reason tooltips, counts badges, >20-row hint. The caller owns the
// assignments state and the suggestion mutations; this component is purely
// presentational.
// ---------------------------------------------------------------------------

interface AssignmentPreviewTableProps {
  /** Rows in preview order (arrival ascending). */
  rows: { id: string; patientName: string; clinicUhid: string | null; date: string | null; arrivalTime?: string | null; oldSlot?: string | null; currentSlot?: string | null; newSlot?: string | null; revertSlot?: string | null; needsAssignment?: boolean; needsRebooking?: boolean; availableSlots?: { key: string; label: string }[] }[];
  counts: { total: number; mapped: number; needsAssignment: number };
  /** Slot-key options per row: (row) => keys. Falls back to a shared list. */
  slotKeysByRow: (row: AssignmentPreviewTableProps["rows"][number]) => string[];
  /** Current manual picks (appointmentId -> slot key). */
  assignments: Record<string, string>;
  onAssign: (appointmentId: string, slotKey: string) => void;
  /** AI suggestion reasons per appointmentId (tooltip on the Select). */
  reasons?: Record<string, string>;
  autoFillPending: boolean;
  suggestPending: boolean;
  refineElapsed: number;
  onAutoFill: () => void;
  /** Admin-only — omit to hide the AI button. */
  onSuggest?: () => void;
  /** Message when there are zero rows. */
  emptyMessage: string;
  /** Column header for the target slot column ("New slot" / "Reverts to"). */
  targetHeader?: string;
}

function AssignmentPreviewTable({
  rows,
  counts,
  slotKeysByRow,
  assignments,
  onAssign,
  reasons = {},
  autoFillPending,
  suggestPending,
  refineElapsed,
  onAutoFill,
  onSuggest,
  emptyMessage,
  targetHeader = "New slot",
}: AssignmentPreviewTableProps) {
  const needsCount = counts.needsAssignment;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge variant="secondary">Total: {counts.total}</Badge>
        <Badge variant="success">Mapped: {counts.mapped}</Badge>
        <Badge variant={needsCount > 0 ? "warning" : "muted"}>Needs assignment: {needsCount}</Badge>
        {needsCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-6 gap-1 px-2 text-xs"
            onClick={onAutoFill}
            disabled={autoFillPending || suggestPending}
            title="Instantly assign each row to the nearest session window with capacity (no AI)"
          >
            {autoFillPending ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
            Auto-fill
          </Button>
        )}
        {onSuggest && needsCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-6 gap-1 px-2 text-xs"
            onClick={onSuggest}
            disabled={suggestPending || autoFillPending}
            title="Ask the AI assistant to review the current assignments and adjust them"
          >
            {suggestPending ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {suggestPending ? `Refining… ${refineElapsed}s` : "Refine with AI"}
          </Button>
        )}
      </div>
      {needsCount > 20 && (
        <p className="text-[11px] text-muted">AI refinement on large previews can take 1–2 minutes.</p>
      )}
      {rows.length === 0 ? (
        <p className="rounded-md bg-cream-100 p-3 text-xs text-muted dark:bg-dark-700">{emptyMessage}</p>
      ) : (
        <div className="max-h-64 overflow-auto rounded-md border border-cream-200 dark:border-dark-700">
          <Table>
            <TableHeader>
              <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                <TableHead>Patient</TableHead>
                <TableHead>UHID</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Old slot</TableHead>
                <TableHead>{targetHeader}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const needs = r.needsAssignment ?? r.needsRebooking ?? false;
                const target = r.newSlot ?? r.revertSlot ?? null;
                const keys = slotKeysByRow(r);
                return (
                  <TableRow key={r.id} className={cn("even:bg-cream-50/50", needs && "bg-amber-50 dark:bg-amber-900/20")}>
                    <TableCell className="text-sm">{r.patientName}</TableCell>
                    <TableCell className="text-xs text-muted">{r.clinicUhid ?? "—"}</TableCell>
                    <TableCell className="text-xs">{r.date ?? "—"}</TableCell>
                    <TableCell className="text-xs">{r.oldSlot ?? r.currentSlot ?? "—"}</TableCell>
                    <TableCell>
                      {target ? (
                        <span className="text-xs font-medium">{target}</span>
                      ) : (
                        <Select
                          value={assignments[r.id] ?? ""}
                          onValueChange={(v) => onAssign(r.id, v)}
                        >
                          <SelectTrigger
                            className="h-7 w-36 text-xs"
                            title={reasons[r.id] ?? undefined}
                          >
                            <SelectValue placeholder="Assign slot…" />
                          </SelectTrigger>
                          <SelectContent>
                            {keys.map((k) => (
                              <SelectItem key={k} value={k}>{k}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The apply wizard (inline multi-step flow with EXPLICIT human go-ahead).
// ---------------------------------------------------------------------------

type WizardStep = 1 | 2 | 3;

function ApplyWizard({ draft, onClose, canEdit, isAdmin }: { draft: SlotConfigVersion; onClose: () => void; canEdit: boolean; isAdmin: boolean }) {
  const [step, setStep] = useState<WizardStep>(1);
  const preview = useSlotConfigPreview(draft.id);
  const triggerBackup = useTriggerSlotConfigBackup();
  const apply = useApplySlotConfig();
  const suggest = useSuggestAssignments();
  const autoFill = useAutoFillAssignments();

  // Manual assignments for needsAssignment rows (appointmentId -> slot key).
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  // AI suggestion reasons per appointmentId (tooltip on the slot Select).
  const [reasons, setReasons] = useState<Record<string, string>>({});
  // Live elapsed-seconds counter while the AI refine call is pending (the
  // 2026-10-18 UX fix: a 71-97s AI call on ~200 rows looked "stuck" — the
  // counter proves it's progressing and sets the expectation of 1-2 min).
  const [refineElapsed, setRefineElapsed] = useState(0);
  useEffect(() => {
    if (!suggest.isPending) {
      setRefineElapsed(0);
      return;
    }
    setRefineElapsed(0);
    const startedAt = Date.now();
    const t = setInterval(() => setRefineElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(t);
  }, [suggest.isPending]);
  // Backup polling state.
  const [backupPolling, setBackupPolling] = useState(false);
  const [backupReady, setBackupReady] = useState(false);
  const [backupCompletedAt, setBackupCompletedAt] = useState<string | null>(null);
  const [backupError, setBackupError] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCount = useRef(0);

  // Step 3 typed confirmation.
  const [confirmed, setConfirmed] = useState(false);
  // Apply result report.
  const [result, setResult] = useState<{ affected: number; perAppointment: { id: string; patientName: string; oldSlot: string | null; newSlot: string | null; queueOrder: number | null }[]; mismatches: { id: string; expectedSlot: string; actualSlot: string | null }[] } | null>(null);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  const rows = preview.data?.rows ?? [];
  const counts = preview.data?.counts ?? { total: 0, mapped: 0, needsAssignment: 0 };
  const draftKeys = useMemo(() => draft.slots.map((s) => s.key), [draft.slots]);
  const allAssigned = rows.every((r) => !r.needsAssignment || assignments[r.id]);

  const startBackup = async () => {
    setBackupError(null);
    setBackupReady(false);
    try {
      await triggerBackup.mutateAsync(draft.id);
      toast.success("Backup triggered — polling until it completes");
      setBackupPolling(true);
      pollCount.current = 0;
      if (pollTimer.current) clearInterval(pollTimer.current);
      pollTimer.current = setInterval(async () => {
        pollCount.current++;
        try {
          const json = await apiFetch<SlotConfigBackupStatus>(`/slot-configs/${draft.id}/backup-status`);
          if (json.ready) {
            setBackupReady(true);
            setBackupCompletedAt(json.completedAt);
            setBackupPolling(false);
            if (pollTimer.current) clearInterval(pollTimer.current);
          } else if (pollCount.current >= 48) {
            // ~8 min at 10s intervals — give up.
            setBackupPolling(false);
            setBackupError(json.reason ?? "Backup did not complete within 8 minutes — try again.");
            if (pollTimer.current) clearInterval(pollTimer.current);
          }
        } catch {
          // transient network failure — keep polling until the cap
        }
      }, 10_000);
    } catch {
      setBackupError("Failed to trigger the backup — check the backup worker configuration.");
    }
  };

  const doApply = async () => {
    try {
      const res = await apply.mutateAsync({
        id: draft.id,
        assignments: Object.entries(assignments).map(([appointmentId, slotKey]) => ({ appointmentId, slotKey })),
      });
      setResult({ affected: res.affected, perAppointment: res.perAppointment, mismatches: res.mismatches });
    } catch {
      // onError shows the toast (e.g. 422 backup_required when the 45-min
      // freshness window lapsed between the poll and the apply).
    }
  };

  // Shared merge for suggestion results (Auto-fill + AI refine): values fill
  // ONLY rows the admin has NOT already picked a value for — manual choices
  // always win. Rows left null by the server stay untouched. Returns the
  // number of rows actually filled.
  const mergeSuggestions = (res: { assignments: { appointmentId: string; slotKey: string | null; reason: string }[] }): number => {
    const applied: Record<string, string> = {};
    const newReasons: Record<string, string> = {};
    for (const a of res.assignments) {
      if (a.slotKey === null) continue;
      if (assignments[a.appointmentId]) continue; // admin already picked — keep theirs
      applied[a.appointmentId] = a.slotKey;
      if (a.reason) newReasons[a.appointmentId] = a.reason;
    }
    if (Object.keys(applied).length > 0) {
      setAssignments((prev) => ({ ...prev, ...applied }));
      setReasons((prev) => ({ ...prev, ...newReasons }));
    }
    return Object.keys(applied).length;
  };

  // Instant deterministic auto-fill (no AI): nearest-window + headroom,
  // completes in well under a second.
  const doAutoFill = async () => {
    try {
      const res = await autoFill.mutateAsync({ configId: draft.id });
      if (res.assignments.length === 0) {
        toast.info(res.message ?? "No appointments need assignment.");
        return;
      }
      const n = mergeSuggestions(res);
      toast.success(`Auto-filled ${n} assignment${n === 1 ? "" : "s"} — review before continuing`);
      const unassigned = res.assignments.filter((a) => a.slotKey === null).length;
      if (unassigned > 0) {
        toast.warning(`${unassigned} row${unassigned === 1 ? "" : "s"} couldn't be auto-filled (slots at capacity) — assign manually.`);
      }
    } catch {
      // onError shows the toast.
    }
  };

  // "Refine with AI" (admin-only): passes the current assignments state so
  // the model REVIEWS/ADJUSTS the mapping instead of generating from scratch.
  const doSuggest = async () => {
    try {
      const res = await suggest.mutateAsync({
        configId: draft.id,
        currentAssignments: Object.entries(assignments).map(([appointmentId, slotKey]) => ({ appointmentId, slotKey })),
      });
      if (res.error === "ai_unavailable") {
        toast.error(res.message ?? "AI is not available right now.");
        return;
      }
      if (res.assignments.length === 0) {
        toast.info(res.message ?? "No appointments need assignment.");
        return;
      }
      const n = mergeSuggestions(res);
      toast.success(`AI suggested ${n} assignment${n === 1 ? "" : "s"} — review before continuing`);
      const unassigned = res.assignments.filter((a) => a.slotKey === null).length;
      if (unassigned > 0) {
        toast.warning(`AI could not assign ${unassigned} row${unassigned === 1 ? "" : "s"} — pick slots for them manually.`);
      }
    } catch {
      // onError shows the toast.
    }
  };

  return (
    <div className="rounded-lg border border-cream-200 bg-cream-50/60 p-4 dark:border-dark-700 dark:bg-dark-800/60">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-maroon-700 dark:text-amber-500">
          <Play size={14} />
          Apply wizard — {draft.effectiveFrom} ({draft.slots.length} slots)
        </div>
        <div className="flex items-center gap-2 text-xs text-muted">
          <span className={cn("rounded-full px-2 py-0.5", step === 1 ? "bg-maroon-600 text-cream-50" : "bg-cream-200 dark:bg-dark-700")}>1 Preview</span>
          <span className={cn("rounded-full px-2 py-0.5", step === 2 ? "bg-maroon-600 text-cream-50" : "bg-cream-200 dark:bg-dark-700")}>2 Backup</span>
          <span className={cn("rounded-full px-2 py-0.5", step === 3 ? "bg-maroon-600 text-cream-50" : "bg-cream-200 dark:bg-dark-700")}>3 Apply</span>
        </div>
        <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
      </div>

      {/* ---------------- Step 1: Preview ---------------- */}
      {step === 1 && (
        <div className="space-y-3">
          <p className="text-xs text-muted">
            Dry-run: every Pending/Booked appointment on/after {draft.effectiveFrom} is mapped by arrival time into the
            new session windows. Rows that fit no window need a manual assignment before apply.
          </p>
          {preview.isLoading && <p className="text-xs text-muted">Loading preview…</p>}
          {preview.isError && <Alert variant="destructive"><AlertDescription>Failed to load the preview.</AlertDescription></Alert>}
          {preview.data && (
            <>
              <AssignmentPreviewTable
                rows={rows}
                counts={counts}
                slotKeysByRow={() => draftKeys}
                assignments={assignments}
                onAssign={(apptId, slotKey) => setAssignments((a) => ({ ...a, [apptId]: slotKey }))}
                reasons={reasons}
                autoFillPending={autoFill.isPending}
                suggestPending={suggest.isPending}
                refineElapsed={refineElapsed}
                onAutoFill={doAutoFill}
                onSuggest={isAdmin ? doSuggest : undefined}
                emptyMessage={`No upcoming appointments fall on/after ${draft.effectiveFrom} — nothing will move. Continue to the backup step.`}
              />
              <div className="flex justify-end">
                <Button
                  size="sm"
                  className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
                  disabled={!allAssigned}
                  onClick={() => setStep(2)}
                >
                  Continue to backup <ArrowDown size={12} className="ml-1" />
                </Button>
              </div>
              {!allAssigned && <p className="text-xs text-red-600">Assign a slot to every highlighted row to continue.</p>}
            </>
          )}
        </div>
      )}

      {/* ---------------- Step 2: Backup (NO SKIP) ---------------- */}
      {step === 2 && (
        <div className="space-y-3">
          <Alert>
            <AlertDescription>
              A fresh database backup (completed within the last 45 minutes) is REQUIRED before apply — the server
              refuses to apply without one. Skipping is not possible. Backups take a few minutes.
            </AlertDescription>
          </Alert>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
              onClick={startBackup}
              disabled={backupPolling || backupReady || triggerBackup.isPending}
            >
              <DatabaseBackup size={14} className="mr-1" />
              {backupReady ? "Backup ready" : backupPolling ? "Polling…" : triggerBackup.isPending ? "Triggering…" : "Run backup now"}
            </Button>
            {backupPolling && <Loader2 size={14} className="animate-spin text-muted" />}
            {backupReady && (
              <span className="text-xs text-green-700 dark:text-green-400">
                Backup completed {backupCompletedAt ? new Date(backupCompletedAt).toLocaleString() : ""}
              </span>
            )}
            {backupError && <span className="text-xs text-red-600">{backupError}</span>}
          </div>
          <div className="flex justify-between">
            <Button variant="outline" size="sm" onClick={() => setStep(1)}><ArrowLeft size={12} className="mr-1" /> Back to preview</Button>
            <Button size="sm" className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" disabled={!backupReady} onClick={() => setStep(3)}>
              Continue to apply <ArrowDown size={12} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* ---------------- Step 3: Apply (typed confirmation) ---------------- */}
      {step === 3 && !result && (
        <div className="space-y-3">
          <div className="rounded-md border border-cream-200 p-3 text-sm dark:border-dark-700">
            <p><strong>{counts.total}</strong> appointment{counts.total === 1 ? "" : "s"} will be remapped to the new slots (effective {draft.effectiveFrom}).</p>
            {Object.keys(assignments).length > 0 && (
              <p className="mt-1 text-xs text-muted">
                Manual assignments: {Object.keys(assignments).length} row{Object.keys(assignments).length === 1 ? "" : "s"}.
              </p>
            )}
            <p className="mt-1 text-xs text-muted">Late policy: {LATE_POLICIES.find((p) => p.value === draft.latePolicy)?.label} ({LATE_POLICIES.find((p) => p.value === draft.latePolicy)?.hint}).</p>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(v === true)} className="mt-0.5" />
            <span>I have reviewed the changes above and want to apply this slot configuration.</span>
          </label>
          <div className="flex justify-between">
            <Button variant="outline" size="sm" onClick={() => setStep(2)}><ArrowLeft size={12} className="mr-1" /> Back to backup</Button>
            <Button
              size="sm"
              className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
              disabled={!confirmed || !canEdit || apply.isPending}
              onClick={doApply}
            >
              {apply.isPending ? <><Loader2 size={14} className="mr-1 animate-spin" /> Applying…</> : "Apply configuration"}
            </Button>
          </div>
        </div>
      )}

      {/* ---------------- Result report ---------------- */}
      {result && (
        <div className="space-y-3">
          {result.mismatches.length === 0 ? (
            <Alert>
              <AlertDescription>
                <strong>Applied successfully.</strong> {result.affected} appointment{result.affected === 1 ? "" : "s"} remapped.
                Queue orders were recomputed per new slot per date.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="destructive">
              <AlertDescription>
                <strong>Applied with {result.mismatches.length} mismatch{result.mismatches.length === 1 ? "" : "es"}.</strong>{" "}
                Some appointments did not land in their computed slots — contact support / check the audit log
                (action APPLY_SLOT_CONFIG) before relying on today's queue.
              </AlertDescription>
            </Alert>
          )}
          {result.perAppointment.length > 0 && (
            <div className="max-h-64 overflow-auto rounded-md border border-cream-200 dark:border-dark-700">
              <Table>
                <TableHeader>
                  <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                    <TableHead>Patient</TableHead>
                    <TableHead>Old slot</TableHead>
                    <TableHead>New slot</TableHead>
                    <TableHead>Queue #</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.perAppointment.map((r) => (
                    <TableRow key={r.id} className="even:bg-cream-50/50">
                      <TableCell className="text-sm">{r.patientName}</TableCell>
                      <TableCell className="text-xs">{r.oldSlot ?? "—"}</TableCell>
                      <TableCell className="text-xs font-medium">{r.newSlot ?? "—"}</TableCell>
                      <TableCell className="text-xs">{r.queueOrder ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="flex justify-end">
            <Button size="sm" onClick={onClose}>Done</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared backup-freshness gate (the wizard's step-2 pattern, extracted for
// reuse by the pending-plan Edit confirm and Delete dialog): trigger the
// backup, poll status every 10s (max ~8 min) until the 45-min freshness rule
// reports ready.
// ---------------------------------------------------------------------------

function useBackupGate(configId: string | null) {
  const triggerBackup = useTriggerSlotConfigBackup();
  const [polling, setPolling] = useState(false);
  const [ready, setReady] = useState(false);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCount = useRef(0);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  const start = async () => {
    if (!configId) return;
    setError(null);
    setReady(false);
    try {
      await triggerBackup.mutateAsync(configId);
      toast.success("Backup triggered — polling until it completes");
      setPolling(true);
      pollCount.current = 0;
      if (pollTimer.current) clearInterval(pollTimer.current);
      pollTimer.current = setInterval(async () => {
        pollCount.current++;
        try {
          const json = await apiFetch<SlotConfigBackupStatus>(`/slot-configs/${configId}/backup-status`);
          if (json.ready) {
            setReady(true);
            setCompletedAt(json.completedAt);
            setPolling(false);
            if (pollTimer.current) clearInterval(pollTimer.current);
          } else if (pollCount.current >= 48) {
            setPolling(false);
            setError(json.reason ?? "Backup did not complete within 8 minutes — try again.");
            if (pollTimer.current) clearInterval(pollTimer.current);
          }
        } catch {
          // transient network failure — keep polling until the cap
        }
      }, 10_000);
    } catch {
      setError("Failed to trigger the backup — check the backup worker configuration.");
    }
  };

  return { start, polling, ready, completedAt, error, pending: triggerBackup.isPending };
}

/** The backup gate UI (trigger button + status line) — shared by the pending
 * plan Edit confirm and Delete dialog. */
function BackupGate({ gate }: { gate: ReturnType<typeof useBackupGate> }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
        onClick={gate.start}
        disabled={gate.polling || gate.ready || gate.pending}
      >
        <DatabaseBackup size={14} className="mr-1" />
        {gate.ready ? "Backup ready" : gate.polling ? "Polling…" : gate.pending ? "Triggering…" : "Run backup now"}
      </Button>
      {gate.polling && <Loader2 size={14} className="animate-spin text-muted" />}
      {gate.ready && (
        <span className="text-xs text-green-700 dark:text-green-400">
          Backup completed {gate.completedAt ? new Date(gate.completedAt).toLocaleString() : ""}
        </span>
      )}
      {gate.error && <span className="text-xs text-red-600">{gate.error}</span>}
    </div>
  );
}

/** The remap/revert report table (shared by apply result, pending-plan edit
 * result, and pending-plan delete result). */
function RemapReport({ report, title }: { report: SlotConfigRemapReport; title: string }) {
  const overCapacity = Object.entries(report.capacity).filter(([, v]) => v.over);
  return (
    <div className="space-y-3">
      {report.mismatches.length === 0 ? (
        <Alert>
          <AlertDescription>
            <strong>{title}.</strong> {report.affected} appointment{report.affected === 1 ? "" : "s"} moved.
            Queue orders were recomputed per slot per date.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="destructive">
          <AlertDescription>
            <strong>{title} with {report.mismatches.length} mismatch{report.mismatches.length === 1 ? "" : "es"}.</strong>{" "}
            Some appointments did not land in their computed slots — check the audit log before relying on the queue.
          </AlertDescription>
        </Alert>
      )}
      {overCapacity.length > 0 && (
        <Alert variant="destructive">
          <AlertDescription>
            <strong>Over capacity:</strong>{" "}
            {overCapacity.map(([k, v]) => `${k}: ${v.booked}/${v.max}`).join(", ")} — the plan allows this, but consider raising maxAppts.
          </AlertDescription>
        </Alert>
      )}
      {report.perAppointment.length > 0 && (
        <div className="max-h-64 overflow-auto rounded-md border border-cream-200 dark:border-dark-700">
          <Table>
            <TableHeader>
              <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                <TableHead>Patient</TableHead>
                <TableHead>Old slot</TableHead>
                <TableHead>New slot</TableHead>
                <TableHead>Queue #</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.perAppointment.map((r) => (
                <TableRow key={r.id} className="even:bg-cream-50/50">
                  <TableCell className="text-sm">{r.patientName}</TableCell>
                  <TableCell className="text-xs">{r.oldSlot ?? "—"}</TableCell>
                  <TableCell className="text-xs font-medium">{r.newSlot ?? "—"}</TableCell>
                  <TableCell className="text-xs">{r.queueOrder ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending-plan Edit wizard (2026-10-18) — mirrors the Apply wizard's 3 steps:
// 1 Preview (edit-preview against the PROPOSED windows + AssignmentPreviewTable
//   with Auto-fill / Refine with AI via proposedSlots),
// 2 Backup (shared gate),
// 3 typed confirm -> PATCH { effectiveFrom?, latePolicy?, slots, assignments }.
// After PATCH: the remap report.
// ---------------------------------------------------------------------------

function PendingPlanEditWizard({
  plan,
  proposed,
  onClose,
  onSave,
  saving,
  isAdmin,
}: {
  plan: SlotConfigVersion;
  /** The proposed edit (validated by the editor before opening). */
  proposed: SlotConfigPayload;
  onClose: () => void;
  onSave: (assignments: { appointmentId: string; slotKey: string }[]) => Promise<SlotConfigRemapReport | null>;
  saving: boolean;
  isAdmin: boolean;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const gate = useBackupGate(plan.id);
  const suggest = useSuggestAssignments();
  const autoFill = useAutoFillAssignments();

  // Edit preview against the PROPOSED windows (manual call — the body is the
  // live proposed edit, so it must not be a cached query).
  const [preview, setPreview] = useState<SlotConfigPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setPreviewLoading(true);
    setPreviewError(null);
    apiFetch<SlotConfigPreview>(`/slot-configs/${plan.id}/edit-preview`, {
      method: "POST",
      body: JSON.stringify({
        effectiveFrom: proposed.effectiveFrom,
        latePolicy: proposed.latePolicy,
        slots: proposed.slots,
      }),
    })
      .then((json) => { if (alive) setPreview(json); })
      .catch(() => { if (alive) setPreviewError("Failed to load the edit preview."); })
      .finally(() => { if (alive) setPreviewLoading(false); });
    return () => { alive = false; };
  }, [plan.id]);

  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [refineElapsed, setRefineElapsed] = useState(0);
  useEffect(() => {
    if (!suggest.isPending) {
      setRefineElapsed(0);
      return;
    }
    setRefineElapsed(0);
    const startedAt = Date.now();
    const t = setInterval(() => setRefineElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(t);
  }, [suggest.isPending]);

  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState<SlotConfigRemapReport | null>(null);

  const rows: SlotConfigPreviewRow[] = preview?.rows ?? [];
  const counts = preview?.counts ?? { total: 0, mapped: 0, needsAssignment: 0 };
  const proposedKeys = useMemo(() => proposed.slots.map((s) => s.key), [proposed.slots]);
  const allAssigned = rows.every((r) => !r.needsAssignment || assignments[r.id]);

  const mergeSuggestions = (res: { assignments: { appointmentId: string; slotKey: string | null; reason: string }[] }): number => {
    const applied: Record<string, string> = {};
    const newReasons: Record<string, string> = {};
    for (const a of res.assignments) {
      if (a.slotKey === null) continue;
      if (assignments[a.appointmentId]) continue; // admin already picked — keep theirs
      applied[a.appointmentId] = a.slotKey;
      if (a.reason) newReasons[a.appointmentId] = a.reason;
    }
    if (Object.keys(applied).length > 0) {
      setAssignments((prev) => ({ ...prev, ...applied }));
      setReasons((prev) => ({ ...prev, ...newReasons }));
    }
    return Object.keys(applied).length;
  };

  const doAutoFill = async () => {
    try {
      const res = await autoFill.mutateAsync({
        configId: plan.id,
        proposedSlots: proposed.slots,
        proposedEffectiveFrom: proposed.effectiveFrom,
      });
      if (res.assignments.length === 0) {
        toast.info(res.message ?? "No appointments need assignment.");
        return;
      }
      const n = mergeSuggestions(res);
      toast.success(`Auto-filled ${n} assignment${n === 1 ? "" : "s"} — review before continuing`);
      const unassigned = res.assignments.filter((a) => a.slotKey === null).length;
      if (unassigned > 0) {
        toast.warning(`${unassigned} row${unassigned === 1 ? "" : "s"} couldn't be auto-filled (slots at capacity) — assign manually.`);
      }
    } catch {
      // onError shows the toast.
    }
  };

  const doSuggest = async () => {
    try {
      const res = await suggest.mutateAsync({
        configId: plan.id,
        proposedSlots: proposed.slots,
        proposedEffectiveFrom: proposed.effectiveFrom,
        currentAssignments: Object.entries(assignments).map(([appointmentId, slotKey]) => ({ appointmentId, slotKey })),
      });
      if (res.error === "ai_unavailable") {
        toast.error(res.message ?? "AI is not available right now.");
        return;
      }
      if (res.assignments.length === 0) {
        toast.info(res.message ?? "No appointments need assignment.");
        return;
      }
      const n = mergeSuggestions(res);
      toast.success(`AI suggested ${n} assignment${n === 1 ? "" : "s"} — review before continuing`);
      const unassigned = res.assignments.filter((a) => a.slotKey === null).length;
      if (unassigned > 0) {
        toast.warning(`AI could not assign ${unassigned} row${unassigned === 1 ? "" : "s"} — pick slots for them manually.`);
      }
    } catch {
      // onError shows the toast.
    }
  };

  const doSave = async () => {
    const report = await onSave(Object.entries(assignments).map(([appointmentId, slotKey]) => ({ appointmentId, slotKey })));
    if (report) setResult(report);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{result ? "Plan updated" : `Edit pending plan — ${plan.effectiveFrom}`}</DialogTitle>
        </DialogHeader>
        {result ? (
          <RemapReport report={result} title="Plan updated" />
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted">
                Saving re-maps every Pending/Booked appointment from {plan.effectiveFrom} into the edited windows.
              </p>
              <div className="flex items-center gap-2 text-xs text-muted">
                <span className={cn("rounded-full px-2 py-0.5", step === 1 ? "bg-maroon-600 text-cream-50" : "bg-cream-200 dark:bg-dark-700")}>1 Preview</span>
                <span className={cn("rounded-full px-2 py-0.5", step === 2 ? "bg-maroon-600 text-cream-50" : "bg-cream-200 dark:bg-dark-700")}>2 Backup</span>
                <span className={cn("rounded-full px-2 py-0.5", step === 3 ? "bg-maroon-600 text-cream-50" : "bg-cream-200 dark:bg-dark-700")}>3 Save</span>
              </div>
            </div>

            {/* ---------------- Step 1: Preview against the PROPOSED windows ---------------- */}
            {step === 1 && (
              <>
                {previewLoading && <p className="text-xs text-muted">Loading preview…</p>}
                {previewError && <Alert variant="destructive"><AlertDescription>{previewError}</AlertDescription></Alert>}
                {preview && (
                  <>
                    <AssignmentPreviewTable
                      rows={rows}
                      counts={counts}
                      slotKeysByRow={() => proposedKeys}
                      assignments={assignments}
                      onAssign={(apptId, slotKey) => setAssignments((a) => ({ ...a, [apptId]: slotKey }))}
                      reasons={reasons}
                      autoFillPending={autoFill.isPending}
                      suggestPending={suggest.isPending}
                      refineElapsed={refineElapsed}
                      onAutoFill={doAutoFill}
                      onSuggest={isAdmin ? doSuggest : undefined}
                      emptyMessage={`No appointments fall on/after ${proposed.effectiveFrom} — nothing will move.`}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
                        disabled={!allAssigned}
                        onClick={() => setStep(2)}
                      >
                        Continue to backup <ArrowDown size={12} className="ml-1" />
                      </Button>
                    </div>
                    {!allAssigned && <p className="text-xs text-red-600">Assign a slot to every highlighted row to continue.</p>}
                  </>
                )}
              </>
            )}

            {/* ---------------- Step 2: Backup ---------------- */}
            {step === 2 && (
              <>
                <Alert>
                  <AlertDescription>
                    A fresh database backup (completed within the last 45 minutes) is REQUIRED — the server refuses the
                    edit without one. Backups take a few minutes.
                  </AlertDescription>
                </Alert>
                <BackupGate gate={gate} />
                <div className="flex justify-between">
                  <Button variant="outline" size="sm" onClick={() => setStep(1)}><ArrowLeft size={12} className="mr-1" /> Back to preview</Button>
                  <Button size="sm" className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" disabled={!gate.ready} onClick={() => setStep(3)}>
                    Continue to save <ArrowDown size={12} className="ml-1" />
                  </Button>
                </div>
              </>
            )}

            {/* ---------------- Step 3: typed confirm ---------------- */}
            {step === 3 && (
              <>
                <div className="rounded-md border border-cream-200 p-3 text-sm dark:border-dark-700">
                  <p><strong>{counts.total}</strong> appointment{counts.total === 1 ? "" : "s"} will be re-mapped into the edited windows (effective {proposed.effectiveFrom}).</p>
                  {Object.keys(assignments).length > 0 && (
                    <p className="mt-1 text-xs text-muted">
                      Manual assignments: {Object.keys(assignments).length} row{Object.keys(assignments).length === 1 ? "" : "s"}.
                    </p>
                  )}
                </div>
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(v === true)} className="mt-0.5" />
                  <span>I have reviewed the preview and want to save this edit and re-map these appointments.</span>
                </label>
                <div className="flex justify-between">
                  <Button variant="outline" size="sm" onClick={() => setStep(2)}><ArrowLeft size={12} className="mr-1" /> Back to backup</Button>
                  <Button
                    size="sm"
                    className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
                    disabled={!confirmed || !gate.ready || saving}
                    onClick={doSave}
                  >
                    {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : "Save + re-map"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
        <DialogFooter>
          {result ? (
            <Button size="sm" onClick={onClose}>Done</Button>
          ) : (
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Pending-plan Delete dialog — the revert preview rendered via the SAME
// AssignmentPreviewTable format as the apply wizard (2026-10-18):
// needsRebooking rows get dropdowns (options from the row's date revert
// config) + Auto-fill + Refine with AI (mode:'revert'). Delete is disabled
// until every needsRebooking row has a slot AND the backup gate is ready.
// If needsRebooking === 0 the table is read-only and Delete is immediately
// available (after the backup gate). DELETE sends { assignments }.
// ---------------------------------------------------------------------------

function PendingPlanDeleteDialog({
  plan,
  onClose,
  onDeleted,
  isAdmin,
}: {
  plan: SlotConfigVersion;
  onClose: () => void;
  onDeleted: (reverted: SlotConfigRemapReport | null) => void;
  isAdmin: boolean;
}) {
  const revertPreview = useSlotConfigRevertPreview(plan.id);
  const gate = useBackupGate(plan.id);
  const del = useDeleteSlotConfig();
  const suggest = useSuggestAssignments();
  const autoFill = useAutoFillAssignments();

  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [refineElapsed, setRefineElapsed] = useState(0);
  useEffect(() => {
    if (!suggest.isPending) {
      setRefineElapsed(0);
      return;
    }
    setRefineElapsed(0);
    const startedAt = Date.now();
    const t = setInterval(() => setRefineElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(t);
  }, [suggest.isPending]);

  const rows = revertPreview.data?.rows ?? [];
  const counts = revertPreview.data?.counts ?? { total: 0, mapped: 0, needsRebooking: 0 };
  const allAssigned = rows.every((r) => !r.needsRebooking || assignments[r.id]);
  const blocked = !allAssigned;

  const mergeSuggestions = (res: { assignments: { appointmentId: string; slotKey: string | null; reason: string }[] }): number => {
    const applied: Record<string, string> = {};
    const newReasons: Record<string, string> = {};
    for (const a of res.assignments) {
      if (a.slotKey === null) continue;
      if (assignments[a.appointmentId]) continue; // admin already picked — keep theirs
      applied[a.appointmentId] = a.slotKey;
      if (a.reason) newReasons[a.appointmentId] = a.reason;
    }
    if (Object.keys(applied).length > 0) {
      setAssignments((prev) => ({ ...prev, ...applied }));
      setReasons((prev) => ({ ...prev, ...newReasons }));
    }
    return Object.keys(applied).length;
  };

  const doAutoFill = async () => {
    try {
      const res = await autoFill.mutateAsync({ configId: plan.id, mode: "revert" });
      if (res.assignments.length === 0) {
        toast.info(res.message ?? "No appointments need assignment.");
        return;
      }
      const n = mergeSuggestions(res);
      toast.success(`Auto-filled ${n} assignment${n === 1 ? "" : "s"} — review before deleting`);
      const unassigned = res.assignments.filter((a) => a.slotKey === null).length;
      if (unassigned > 0) {
        toast.warning(`${unassigned} row${unassigned === 1 ? "" : "s"} couldn't be auto-filled (slots at capacity) — assign manually.`);
      }
    } catch {
      // onError shows the toast.
    }
  };

  const doSuggest = async () => {
    try {
      const res = await suggest.mutateAsync({
        configId: plan.id,
        mode: "revert",
        currentAssignments: Object.entries(assignments).map(([appointmentId, slotKey]) => ({ appointmentId, slotKey })),
      });
      if (res.error === "ai_unavailable") {
        toast.error(res.message ?? "AI is not available right now.");
        return;
      }
      if (res.assignments.length === 0) {
        toast.info(res.message ?? "No appointments need assignment.");
        return;
      }
      const n = mergeSuggestions(res);
      toast.success(`AI suggested ${n} assignment${n === 1 ? "" : "s"} — review before deleting`);
      const unassigned = res.assignments.filter((a) => a.slotKey === null).length;
      if (unassigned > 0) {
        toast.warning(`AI could not assign ${unassigned} row${unassigned === 1 ? "" : "s"} — pick slots for them manually.`);
      }
    } catch {
      // onError shows the toast.
    }
  };

  const doDelete = async () => {
    try {
      const res = await del.mutateAsync({
        id: plan.id,
        assignments: Object.entries(assignments).map(([appointmentId, slotKey]) => ({ appointmentId, slotKey })),
      });
      onDeleted(res.reverted ?? null);
    } catch {
      // onError shows the toast (e.g. 422 backup_required when the 45-min
      // freshness window lapsed between the poll and the delete).
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Delete pending plan ({plan.effectiveFrom})?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted">
            Deleting this plan reverts every Pending/Booked appointment from {plan.effectiveFrom} onward back to the
            previous policy (by arrival time). This cannot be undone.
          </p>
          {revertPreview.isLoading && <p className="text-xs text-muted">Loading revert preview…</p>}
          {revertPreview.isError && <Alert variant="destructive"><AlertDescription>Failed to load the revert preview.</AlertDescription></Alert>}
          {revertPreview.data && (
            <>
              <AssignmentPreviewTable
                rows={rows}
                counts={{ total: counts.total, mapped: counts.mapped, needsAssignment: counts.needsRebooking }}
                slotKeysByRow={(r) => (r.availableSlots?.length ? r.availableSlots.map((s) => s.key) : [])}
                assignments={assignments}
                onAssign={(apptId, slotKey) => setAssignments((a) => ({ ...a, [apptId]: slotKey }))}
                reasons={reasons}
                autoFillPending={autoFill.isPending}
                suggestPending={suggest.isPending}
                refineElapsed={refineElapsed}
                onAutoFill={doAutoFill}
                onSuggest={isAdmin ? doSuggest : undefined}
                emptyMessage={`No appointments fall on/after ${plan.effectiveFrom} — nothing will revert.`}
                targetHeader="Reverts to"
              />
              {blocked && (
                <Alert variant="destructive">
                  <AlertDescription>
                    <strong>{counts.needsRebooking} appointment{counts.needsRebooking === 1 ? "" : "s"} fit no window of the previous policy.</strong>{" "}
                    Assign each one a slot above (or rebook/cancel it) — the server refuses the delete while any remain unassigned.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
          <Alert>
            <AlertDescription>
              A fresh database backup (completed within the last 45 minutes) is REQUIRED — the server refuses the delete
              without one.
            </AlertDescription>
          </Alert>
          <BackupGate gate={gate} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={del.isPending}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={blocked || !gate.ready || del.isPending}
            onClick={doDelete}
          >
            {del.isPending ? <><Loader2 size={14} className="mr-1 animate-spin" /> Deleting…</> : "Delete plan + revert"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Operational editor (admin-only) — latePolicy + check-in windows on the
// ACTIVE config. Session times/max are version-locked once applied.
// ---------------------------------------------------------------------------

function OperationalEditor({ config }: { config: SlotConfigVersion }) {
  const [latePolicy, setLatePolicy] = useState<SlotConfigPayload["latePolicy"]>(config.latePolicy);
  const [windows, setWindows] = useState<Record<string, { checkinStart: string; checkinEnd: string }>>(() => {
    const init: Record<string, { checkinStart: string; checkinEnd: string }> = {};
    for (const s of [...config.slots].sort((a, b) => a.sortOrder - b.sortOrder)) {
      init[s.key] = { checkinStart: s.checkinStart, checkinEnd: s.checkinEnd };
    }
    return init;
  });
  const update = useUpdateSlotConfigOperational();

  const save = async () => {
    // Client-side HH:mm + ordering validation (the API re-validates).
    for (const [key, w] of Object.entries(windows)) {
      if (!HHMM_RE.test(w.checkinStart) || !HHMM_RE.test(w.checkinEnd)) {
        toast.error(`Slot ${key}: check-in window must be HH:mm (24h).`);
        return;
      }
      if (hhmmToMin(w.checkinStart) > hhmmToMin(w.checkinEnd)) {
        toast.error(`Slot ${key}: check-in start must be <= check-in end.`);
        return;
      }
    }
    try {
      await update.mutateAsync({
        id: config.id,
        body: {
          latePolicy,
          slots: Object.entries(windows).map(([key, w]) => ({ key, checkinStart: w.checkinStart, checkinEnd: w.checkinEnd })),
        },
      });
    } catch { /* toast shown by onError */ }
  };

  return (
    <div className="space-y-3 rounded-lg border border-cream-200 p-3 dark:border-dark-700">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Operational settings (active config)</span>
        <Button size="sm" className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={save} disabled={update.isPending}>
          <Save size={14} className="mr-1" /> Save
        </Button>
      </div>
      <p className="text-xs text-muted">
        Check-in windows and the late policy can be changed any time (they never move appointments). Session times,
        capacity and slot order are version-locked once applied — create a new future version to change those.
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label>Late check-in policy</Label>
          <Select value={latePolicy} onValueChange={(v) => setLatePolicy(v as SlotConfigPayload["latePolicy"])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {LATE_POLICIES.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label} — {p.hint}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
        {Object.entries(windows).map(([key, w]) => (
          <div key={key} className="rounded-md border border-cream-200 p-2 dark:border-dark-700">
            <div className="mb-1 text-xs font-medium">{key}</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Check-in start</Label>
                <Input
                  type="time"
                  value={w.checkinStart}
                  onChange={(e) => setWindows((ws) => ({ ...ws, [key]: { ...ws[key], checkinStart: e.target.value } }))}
                  className="h-8 text-xs"
                />
              </div>
              <div>
                <Label className="text-[10px]">Check-in end</Label>
                <Input
                  type="time"
                  value={w.checkinEnd}
                  onChange={(e) => setWindows((ws) => ({ ...ws, [key]: { ...ws[key], checkinEnd: e.target.value } }))}
                  className="h-8 text-xs"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main card
// ---------------------------------------------------------------------------

export function SlotConfigCard({ settings, canEdit, isAdmin }: {
  settings: AppSettings;
  canEdit: boolean;
  isAdmin: boolean;
}) {
  const { data, isLoading } = useSlotConfigs();
  const create = useCreateSlotConfig();
  const update = useUpdateSlotConfig();
  const del = useDeleteSlotConfig();

  const today = todayIST();
  const configs = useMemo(
    () => [...(data?.rows ?? [])].sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : a.effectiveFrom > b.effectiveFrom ? -1 : 0)),
    [data],
  );
  // The ACTIVE config: the applied config with the greatest effectiveFrom <= today.
  const activeConfig = useMemo(() => {
    const applied = configs.filter((c) => c.appliedAt && c.effectiveFrom <= today);
    return applied.length > 0 ? applied[0] : null;
  }, [configs, today]);
  // The single FUTURE plan (draft OR pending): the one config with
  // effectiveFrom > today. When it exists, the "New draft version" button is
  // hidden (the API 409s a second future plan anyway).
  const futurePlan = useMemo(() => configs.find((c) => c.effectiveFrom > today) ?? null, [configs, today]);

  // Editor state: null = list view; { mode: "create" } | { mode: "edit", id, pending } = editor open.
  const [editor, setEditor] = useState<{ mode: "create"; pending: false } | { mode: "edit"; id: string; pending: boolean } | null>(null);
  const [form, setForm] = useState<DraftForm>({ effectiveFrom: tomorrowIST(), latePolicy: "last", slots: [emptySlotRow(1)] });
  const [errors, setErrors] = useState<string[]>([]);
  // Delete confirm dialog (drafts).
  const [deleteId, setDeleteId] = useState<string | null>(null);
  // Wizard target.
  const [wizardId, setWizardId] = useState<string | null>(null);
  // Pending-plan edit WIZARD (2026-10-18): opened after the editor's Save —
  // carries the PROPOSED payload (validated) for the edit-preview + PATCH.
  const [pendingEdit, setPendingEdit] = useState<SlotConfigPayload | null>(null);
  // Pending-plan delete dialog target.
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const openCreate = () => {
    setForm({ effectiveFrom: tomorrowIST(), latePolicy: "last", slots: [emptySlotRow(1)] });
    setErrors([]);
    setEditor({ mode: "create", pending: false });
  };
  const openEdit = (v: SlotConfigVersion) => {
    setForm(draftFromVersion(v));
    setErrors([]);
    setEditor({ mode: "edit", id: v.id, pending: !!(v.appliedAt && v.effectiveFrom > today) });
  };

  // Editor Save: drafts save directly; pending plans open the edit WIZARD
  // (preview against the proposed windows → backup → typed confirm → PATCH).
  const saveDraft = () => {
    const errs = validateDraft(form);
    setErrors(errs);
    if (errs.length > 0) return;
    if (editor?.mode === "edit" && editor.pending) {
      setPendingEdit(draftToPayload(form));
      return;
    }
    void doSave([]);
  };

  const doSave = async (assignments: { appointmentId: string; slotKey: string }[]): Promise<SlotConfigRemapReport | null> => {
    if (!editor) return null;
    try {
      if (editor.mode === "edit") {
        const res = await update.mutateAsync({ id: editor.id, body: draftToPayload(form), assignments });
        if (res.remap) return res.remap;
        setEditor(null);
        return null;
      }
      await create.mutateAsync(draftToPayload(form));
      setEditor(null);
      return null;
    } catch {
      // toast shown by onError (409 future_plan_exists / config_exists,
      // 422 validation / backup_required, 400 appointments_not_all_moved).
      return null;
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await del.mutateAsync({ id: deleteId });
    } catch { /* toast shown */ }
    setDeleteId(null);
  };

  const wizardDraft = wizardId ? configs.find((c) => c.id === wizardId && !c.appliedAt) ?? null : null;
  const pendingEditPlan = pendingEdit && editor?.mode === "edit" && editor.pending ? configs.find((c) => c.id === editor.id) ?? null : null;
  const pendingDeletePlan = pendingDeleteId ? configs.find((c) => c.id === pendingDeleteId) ?? null : null;

  const updateSlot = (idx: number, patch: Partial<SlotRow>) => {
    setForm((f) => ({ ...f, slots: f.slots.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }));
  };
  const addSlot = () => {
    setForm((f) => ({ ...f, slots: [...f.slots, emptySlotRow(f.slots.length + 1)] }));
  };
  const removeSlot = (idx: number) => {
    setForm((f) => ({ ...f, slots: f.slots.filter((_, i) => i !== idx) }));
  };
  const moveSlot = (idx: number, dir: -1 | 1) => {
    setForm((f) => {
      const slots = [...f.slots];
      const target = idx + dir;
      if (target < 0 || target >= slots.length) return f;
      [slots[idx], slots[target]] = [slots[target], slots[idx]];
      return { ...f, slots };
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-serif text-lg text-maroon-700 dark:text-amber-500">Slot Configurations</CardTitle>
        {canEdit && !editor && !futurePlan && (
          <Button onClick={openCreate} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            <Plus size={14} className="mr-1" /> New draft version
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted">
          Slot configurations are effective-dated: a date uses the applied config with the greatest effectiveFrom on/before it.
          Only ONE future plan (draft or applied-pending) may exist at a time — edit or delete it to make room for another.
          Drafts are future versions — edit them freely, then apply through the audited wizard (preview → backup → apply).
          The legacy Morning/Evening globals below the list are only the fallback for pre-config databases.
        </p>

        {/* ---------------- Draft editor ---------------- */}
        {editor && (
          <div className="space-y-3 rounded-lg border border-cream-200 p-4 dark:border-dark-700">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-maroon-700 dark:text-amber-500">
                {editor.mode === "create"
                  ? "New draft version"
                  : editor.pending
                    ? `Edit pending plan — effective ${form.effectiveFrom}`
                    : `Edit draft — effective ${form.effectiveFrom}`}
              </span>
              <Button variant="outline" size="sm" onClick={() => setEditor(null)}>Cancel</Button>
            </div>
            {editor.pending && (
              <Alert>
                <AlertDescription>
                  This plan is applied and pending (effective date still ahead). Saving re-maps every Pending/Booked
                  appointment from its effective date into the edited windows — a fresh backup is required first.
                </AlertDescription>
              </Alert>
            )}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Label>Effective from (future date, IST)</Label>
                <Input
                  type="date"
                  min={tomorrowIST()}
                  value={form.effectiveFrom}
                  disabled={!canEdit}
                  onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
                />
              </div>
              <div>
                <Label>Late check-in policy</Label>
                <Select value={form.latePolicy} onValueChange={(v) => setForm({ ...form, latePolicy: v as SlotConfigPayload["latePolicy"] })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LATE_POLICIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label} — {p.hint}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              {form.slots.map((s, idx) => (
                <div key={idx} className="rounded-md border border-cream-200 p-3 dark:border-dark-700">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium">Slot {idx + 1}</span>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveSlot(idx, -1)} disabled={idx === 0} title="Move up">
                        <ArrowUp size={12} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveSlot(idx, 1)} disabled={idx === form.slots.length - 1} title="Move down">
                        <ArrowDown size={12} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-red-600" onClick={() => removeSlot(idx)} disabled={form.slots.length <= 1} title="Remove slot">
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                    <div>
                      <Label className="text-[10px]">Key</Label>
                      <Input value={s.key} disabled={!canEdit} onChange={(e) => updateSlot(idx, { key: e.target.value })} placeholder="Morning" className="h-8 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[10px]">Label</Label>
                      <Input value={s.label} disabled={!canEdit} onChange={(e) => updateSlot(idx, { label: e.target.value })} placeholder="Morning session" className="h-8 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[10px]">Session start</Label>
                      <Input type="time" value={s.startTime} disabled={!canEdit} onChange={(e) => updateSlot(idx, { startTime: e.target.value })} className="h-8 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[10px]">Session end</Label>
                      <Input type="time" value={s.endTime} disabled={!canEdit} onChange={(e) => updateSlot(idx, { endTime: e.target.value })} className="h-8 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[10px]">Check-in start</Label>
                      <Input type="time" value={s.checkinStart} disabled={!canEdit} onChange={(e) => updateSlot(idx, { checkinStart: e.target.value })} className="h-8 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[10px]">Check-in end</Label>
                      <Input type="time" value={s.checkinEnd} disabled={!canEdit} onChange={(e) => updateSlot(idx, { checkinEnd: e.target.value })} className="h-8 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[10px]">Max appts</Label>
                      <Input type="number" min={0} value={s.maxAppts} disabled={!canEdit} onChange={(e) => updateSlot(idx, { maxAppts: e.target.value })} className="h-8 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[10px]">Sort order</Label>
                      <Input type="number" value={s.sortOrder} disabled={!canEdit} onChange={(e) => updateSlot(idx, { sortOrder: e.target.value })} className="h-8 text-xs" />
                    </div>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addSlot} disabled={!canEdit}>
                <Plus size={12} className="mr-1" /> Add slot
              </Button>
            </div>

            {errors.length > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  <ul className="list-disc pl-4">
                    {errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end">
              <Button onClick={saveDraft} disabled={!canEdit || create.isPending || update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
                <Save size={14} className="mr-1" />{" "}
                {editor.mode === "edit" ? (editor.pending ? "Save + re-map…" : "Save draft") : "Create draft"}
              </Button>
            </div>
          </div>
        )}

        {/* ---------------- Apply wizard (inline) ---------------- */}
        {wizardDraft && <ApplyWizard draft={wizardDraft} canEdit={canEdit} isAdmin={isAdmin} onClose={() => setWizardId(null)} />}

        {/* ---------------- Versions list ---------------- */}
        {!editor && (
          isLoading ? (
            <p className="text-xs text-muted">Loading configurations…</p>
          ) : configs.length === 0 ? (
            <p className="text-xs text-muted">No slot configurations exist yet.</p>
          ) : (
            <div className="space-y-2">
              {configs.map((v) => {
                const isActive = activeConfig?.id === v.id;
                const isDraft = !v.appliedAt;
                const isPendingPlan = !!v.appliedAt && v.effectiveFrom > today;
                return (
                  <div
                    key={v.id}
                    className={cn(
                      "rounded-lg border p-3",
                      isActive
                        ? "border-maroon-400 bg-cream-100 dark:border-amber-600 dark:bg-dark-700"
                        : "border-cream-200 dark:border-dark-700",
                    )}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium">Effective {v.effectiveFrom}</span>
                        {isActive && <Badge>Active today</Badge>}
                        {isDraft ? (
                          <Badge variant="warning">Draft</Badge>
                        ) : isPendingPlan ? (
                          <Badge variant="warning">Pending plan — applied {v.appliedAt ? new Date(v.appliedAt).toLocaleDateString() : ""}</Badge>
                        ) : (
                          <Badge variant="secondary">Applied {v.appliedAt ? new Date(v.appliedAt).toLocaleDateString() : ""}</Badge>
                        )}
                        <Badge variant="muted">{v.slots.length} slot{v.slots.length === 1 ? "" : "s"}</Badge>
                        <Badge variant="outline">Late: {LATE_POLICIES.find((p) => p.value === v.latePolicy)?.label}</Badge>
                      </div>
                      {canEdit && isDraft && (
                        <div className="flex items-center gap-1">
                          <Button variant="outline" size="sm" onClick={() => openEdit(v)}><Pencil size={12} className="mr-1" /> Edit</Button>
                          {isAdmin && (
                            <Button variant="outline" size="sm" onClick={() => setWizardId(v.id)}><Play size={12} className="mr-1" /> Apply…</Button>
                          )}
                          <Button variant="outline" size="sm" className="text-red-600" onClick={() => setDeleteId(v.id)}><Trash2 size={12} className="mr-1" /> Delete</Button>
                        </div>
                      )}
                      {canEdit && isPendingPlan && (
                        <div className="flex items-center gap-1">
                          <Button variant="outline" size="sm" onClick={() => openEdit(v)}><Pencil size={12} className="mr-1" /> Edit</Button>
                          <Button variant="outline" size="sm" className="text-red-600" onClick={() => setPendingDeleteId(v.id)}><Trash2 size={12} className="mr-1" /> Delete</Button>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {[...v.slots].sort((a, b) => a.sortOrder - b.sortOrder).map((s) => (
                        <span key={s.key} className="rounded-full bg-cream-200 px-2 py-0.5 text-[11px] dark:bg-dark-700">
                          {s.label}: {s.startTime}–{s.endTime} (check-in {s.checkinStart}–{s.checkinEnd}, max {s.maxAppts})
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* ---------------- Operational editor (admin-only, active config) ---------------- */}
        {isAdmin && activeConfig && !editor && <OperationalEditor config={activeConfig} />}

        {/* ---------------- Pending-plan edit wizard (preview + backup + confirm) ---------------- */}
        {pendingEdit && pendingEditPlan && (
          <PendingPlanEditWizard
            plan={pendingEditPlan}
            proposed={pendingEdit}
            saving={update.isPending}
            isAdmin={isAdmin}
            onClose={() => {
              setPendingEdit(null);
              setEditor(null);
            }}
            onSave={(assignments) => doSave(assignments)}
          />
        )}

        {/* ---------------- Pending-plan delete dialog (revert preview + assignments + backup gate) ---------------- */}
        {pendingDeletePlan && (
          <PendingPlanDeleteDialog
            plan={pendingDeletePlan}
            isAdmin={isAdmin}
            onClose={() => setPendingDeleteId(null)}
            onDeleted={() => setPendingDeleteId(null)}
          />
        )}

        {/* ---------------- Delete confirm dialog (drafts) ---------------- */}
        <Dialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete this draft?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted">The draft will be removed. This cannot be undone (applied configs cannot be deleted — only drafts and pending plans).</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button variant="destructive" onClick={confirmDelete} disabled={del.isPending}>Delete draft</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Legacy globals note — the fields remain in app_settings as the
            pre-config fallback; the editor for them was replaced by this card. */}
        <p className="text-[11px] text-muted">
          Legacy fallback (pre-config databases): Morning {settings.morningSlotStart}–{settings.morningSlotEnd} (max {settings.maxMorningAppts}),
          Evening {settings.eveningSlotStart}–{settings.eveningSlotEnd} (max {settings.maxEveningAppts}).
        </p>
      </CardContent>
    </Card>
  );
}
